import React from 'react';
import { Redirect } from 'react-router';

import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import PeopleIcon from '@material-ui/icons/People';
import Typography from "@material-ui/core/Typography";
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined';

import Api from '../../../../../../common/api'
import Local from '../../../../../../common/local'
import Util from '../../../../../../common/util';
import Recaptcha from '../../../../../../common/components/recaptcha';

import CurrentUser from '../../../../shared/components/current-user';
import Heading from '../../../../shared/components/heading';
import LeftRail from '../../../../shared/components/left-rail';
import PageHeader from '../../../../shared/components/page-header';
import StandardInput from '../../../../shared/components/gn-standard-input';
import withTranslations from '../../../../../../common/components/with-translations';

const styles = (theme) => ({
  view: {
    ...theme.internalContainer,
    [theme.breakpoints.up('md')]: {
      padding: theme.internalContainer._themeMdPadding
    },
  },
  button: {
    borderRadius: '9999px',
    fontWeight: '600',
    fontSize: '16px',
    height: '55px',
    width: '100%',
  },
  buttonHover: {
    '&:hover': {
      backgroundColor: 'var(--button-hover-color) !important'
    },
  },
  buttonControls: {
    display: "flex",
    justifyContent: "center",
    marginTop: "15px"
  },
  createAccountText: {
    fontSize: '14px',
    paddingTop: '10px',
    textAlign: 'center'
  },
  forgotPasswordText: {
    fontSize: '14px',
    paddingTop: '20px',
    textAlign: 'center'
  },
  linkText: {
    color: '#222222',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  noHeaderContainer: {
    padding: '20px 20px 0px 20px',
    maxWidth: '1200px',
    margin: '0 auto 50px auto'
  },
  stepTitle: {
    display: "flex",
    justifyContent: "center",
    fontWeight: '600',
    fontSize: '1.2rem',
    marginBottom: '10px',
  }
});

class StepSignIn extends React.Component {

  constructor(props) {

    super(props);

    this.state = {
      password: '',
      processing: false,
      showPassword: false,
      username: props.username ? props.username : '',
      password: '',
      validation: {
        password: null,
        username: null,
      }
    };

    this.api = new Api();
    this.generateOnInput = this.generateOnInput.bind(this);
    this.goCreateAccount = this.goCreateAccount.bind(this);
    this.goForgotPassword = this.goForgotPassword.bind(this);
    this.onError = this.onError.bind(this);
    this.onLogin = this.onLogin.bind(this);
    this.onShowPassword = this.onShowPassword.bind(this);
    this.onSubmitPressed = this.onSubmitPressed.bind(this);
    this.startLogin = this.startLogin.bind(this);
  }

  generateOnInput(stateKey) {

    return function (e) {

      this.setState({
        [stateKey]: e.target.value,
        validation: { username: null, password: null }
      });
    }.bind(this);
  }

  goCreateAccount(e) {
    e.preventDefault();
    this.props.goCreateAccount();
  }

  goForgotPassword(e) {
    e.preventDefault();
    this.props.goForgotPassword(this.state.username);
  }

  onError(errObj) {

    this.setState({ processing: false });
    this.props.onError(errObj);
  }

  async onLogin(recaptcha) {
    const { t } = this.props;
    const {
      username,
      password
    } = this.state;

    const validation = {
      username: username === '' ? t('stepSignIn.requiredEmailError') : null,
      password: password === '' ? t('stepSignIn.requiredPasswordError') : null
    };

    if (Object.values(validation).some((v) => { return v !== null; })) {
      return this.setState({ validation });
    }

    this.setState({ processing: true });
    const that = this;

    try {
      const response = await this.api.post(
        `/user/authenticate${this.props.clientId ? `?clientId=${this.props.clientId}` : ''}`,
        {
          username,
          password,
          recaptcha,
          parameters: Util.toCoreQueryObject(this.props.query)
        }
      );

      if (!response.code) {
        this.props.postMessageRaw({
          data: {
            message: 'An error occurred while logging in.',
            statusCode: 500
          },
          event: 'login',
          success: false,
        });
        return this.onError({
          error: t('stepSignIn.serverError'), critical: true
        });
      }

      if (response.code >= 400) {
        this.props.postMessageRaw({
          data: {
            message: response.content.message || 'An error occurred while logging in.',
            statusCode: response.code
          },
          event: 'login',
          success: false,
        });
      }

      if (response.code === 400) {
        return this.onError({
          error: function () {
            if (response.content.validation && response.content.validation.keys.length && response.content.validation.keys[0] === 'username') {
              validation.username = t('stepSignIn.validEmailError');
              that.setState({ validation });
              return t('stepSignIn.invalidEmailError');
            }
            return undefined;
          }() || t('stepSignIn.emailMistakeError')
        });
      }

      if (response.code === 401) {
        return this.onError({
          error: t('stepSignIn.userNotFoundError')
        });
      }
      else if (response.code === 422) {
        return this.props.onRequireResetPassword(username);
      }
      else if (response.code === 423) {
        return this.onError({
          error: t('stepSignIn.accountLockedError')
        });
      }
      else if (response.code === 417) {
        return this.props.onTwoFactor(username, password);
      }
      else if (response.code === 426) {
        return this.props.onUpgradeAccount(username);
      }
      else if (response.code === 428) {
        return this.props.onRequiresReconciliation(username);
      }

      if (!response.content || !response.content.token) {
        this.props.postMessageRaw({
          data: {
            message: 'An error occurred while logging in.',
            statusCode: 500
          },
          event: 'login',
          success: false,
        });
        return this.onError({
          error: t('stepSignIn.serverError'), critical: true
        });
      }

      Local.userLogin(response.content.token, username);
      this.setState({ processing: false });
      return this.props.onLoginSuccess();
    }
    catch (e) {
      return this.onError({ error: e.toString() });
    }
  }

  onShowPassword() {
    this.setState({ showPassword: !this.state.showPassword });
  }

  async onSubmitPressed(e) {

    if (e.key === 'Enter') {
      return await this.startLogin();
    }
  }

  redirect(redirect) {
    this.setState({ redirect });
  }

  async startLogin() {
    if (window.globals && window.globals.gidRecaptchaKey) {
      const that = this;
      window.grecaptcha.ready(function () {
        window.grecaptcha.execute(window.globals.gidRecaptchaKey, { action: 'submit' }).then(async function (token) {
          await that.onLogin(token);
        });
      });
    } else {
      await this.onLogin();
    }
  }

  render() {
    const { t } = this.props;
    const containerStyle =
      this.props.query.noHeader ?
        this.props.classes.noHeaderContainer :
        this.props.classes.view;

    const displayableContent = this.props.application && this.props.application.displayable && this.props.application.displayable.content
      ? this.props.application.displayable.content
      : {};

    const buttonColor = displayableContent.buttonColor || (this.props.query.buttonColor && `#${this.props.query.buttonColor}`);
    const buttonHoverColor = displayableContent.buttonHoverColor || (this.props.query.buttonHoverColor && `#${this.props.query.buttonHoverColor}`);

    const buttonStyle = {
      ...(buttonColor && { backgroundColor: buttonColor }),
      ...(buttonHoverColor && { '--button-hover-color': buttonHoverColor }),
    };

    const buttonClassName = buttonHoverColor ?
      `${this.props.classes.button} ${this.props.classes.buttonHover}` :
      this.props.classes.button;

    const bottomControls = (
      <Grid className={this.props.classes.buttonControls} item xs={12}>
        <Button style={buttonStyle} className={buttonClassName} color="primary" onClick={this.startLogin} variant="contained" id="login">
          {this.state.processing ?
            (<CircularProgress size={14} color="inherit" />)
            : t('stepSignIn.loginButton')
          }
        </Button>
      </Grid>
    );

    const endAdornment = this.state.showPassword ?
      <VisibilityOffIcon color="action" style={{ 'cursor': 'pointer' }} onClick={this.onShowPassword} /> :
      <VisibilityIcon color="action" style={{ 'cursor': 'pointer' }} onClick={this.onShowPassword} />;

    const endAdornmentWrapper = (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={this.onShowPassword}
          edge="end"
        >
          {this.state.showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </IconButton>
      </InputAdornment>
    )

    const focusEmail = this.state.username ? false : true;
    const focusPassword = !focusEmail;

    const emailInput = (
      <Grid item xs={12} md={12}>
        <StandardInput
          inputType="email"
          value={this.state.username}
          style={{ marginBottom: '15px' }}
          error={this.state.validation.username}
          attribute="username"
          label={t('stepSignIn.emailLabel')}
          labelWidth={85}
          focus={focusEmail}
          onChange={this.generateOnInput('username')}
          onKeyUp={this.onSubmitPressed} />
      </Grid>
    );

    const passwordInput = (
      <Grid item xs={12} md={12}>
        <StandardInput
          inputType={this.state.showPassword ? 'text' : 'password'}
          style={{ marginBottom: '15px' }}
          error={this.state.validation.password}
          password={true}
          attribute="password"
          label={t('stepSignIn.passwordLabel')}
          labelWidth={60}
          focus={focusPassword}
          endAdornment={endAdornment}
          onChange={this.generateOnInput('password')}
          onKeyUp={this.onSubmitPressed} />
      </Grid>
    );

    const recaptcha = (window.globals && window.globals.gidRecaptchaKey && !this.props.query.noForgot) ?
      <Recaptcha textClassname={this.props.classes.recaptchaText} /> : null;

    const stepTitle = this.props.query.showSteps === 'true' ? (
      <Grid className={this.props.classes.stepTitle} item xs={12}>
        <Typography className={this.props.classes.stepTitle}>
          {t('stepSignIn.stepTitle')}
        </Typography>
      </Grid>
    ) : null;

    return (
      <Grid container spacing={1} className={containerStyle} >
        {stepTitle}
        {emailInput}
        {passwordInput}
        {recaptcha}
        {bottomControls}
        <Grid item xs={12} md={12}>
          <Typography className={this.props.classes.forgotPasswordText} color="initial" >
            <Link className={this.props.classes.linkText} onClick={this.goForgotPassword} to="/gn">{t('stepSignIn.forgotPasswordLinkText')}</Link>
          </Typography>
        </Grid>
        <Grid item xs={12} md={12}>
          <Typography className={this.props.classes.createAccountText} color="initial" >
            {t('stepSignIn.createAccountQuestion', {
              link: (
                <Link className={this.props.classes.linkText} onClick={this.goCreateAccount} to="/gn">{t('stepSignIn.createAccountLinkText')}</Link>
              )
            })}
          </Typography>
        </Grid>
      </Grid>
    );
  }
}

export default withTranslations(withStyles(styles)(StepSignIn));
